"use client";

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useSyncExternalStore,
  startTransition,
  type ReactNode,
} from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import type { KundaliResponse } from "@/lib/api/kundali";
import {
  makeHandleSubmit,
  makeRetryKundali,
  type KundaliRetryState,
  type HandleSubmitDeps,
} from "./KundaliFormCard.submit";
import { useFieldFunnel } from "./useFieldFunnel";
// Kundali-form analytics imports go straight to the source module instead of
// re-routing through `@/lib/analytics`. The barrel re-export there
// (`export * from "@/lib/analytics-kundali"`) created a chunk-resolution race
// in production builds — `(0, o.trackKundaliFormStarted) is not a function`
// (Sentry 7481455768) fired when the consumer chunk loaded before the kundali
// chunk hydrated the namespace object. Direct imports avoid the indirection.
import { createEventId } from "@/lib/beacon-types";
import { trackKundaliFormStarted } from "@/lib/analytics-kundali";
import {
  DailyInsightSlot,
  KundaliRetryCard,
  useDeferredDailyInsight,
} from "./KundaliFormCard.controls";
import { KundaliInputForm, type CapturedSEOEntry } from "./KundaliFormCard.parts";
import type { KundaliProgressiveCopy } from "./KundaliFormCard.progressive";
import {
  getKundaliTranslations,
  type KundaliLang,
  type KundaliLocale,
} from "./KundaliFormCard.i18n";
import { REGIONAL_KUNDALI_FORM_COPY } from "./KundaliFormCard.i18n.regional";
import type { KundaliRegionalLocale } from "@/i18n/regional-locales";
import { LOADING_STATUS_MESSAGES, loadingStatusTimers } from "./KundaliFormCard.loading";
import { formBannerSEOEntry, useCapturedSEOEntry } from "./KundaliFormCard.seo";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";
import DailyPanchangLink from "@/components/DailyPanchangLink";
import KundaliSavedLink from "./KundaliSavedLink";
import type { KundaliField } from "@/lib/analytics-kundali";
import { useInitialKundaliFieldFocus } from "./useInitialKundaliFieldFocus";
import { KundaliResultBoundary } from "./KundaliFormCard.result";
import {
  useKundaliFieldState,
  useKundaliTranslationBundle,
  type KundaliInitialFieldValue,
} from "./KundaliFormCard.state";

const SpiritualReveal = dynamic(() => import("@ishvaram/ui/SpiritualReveal"), { ssr: false });
const POST_RESULT_ADJUNCTS_MIN_HEIGHT = 220;
const DAILY_PANCHANG_LINK_MIN_HEIGHT = 92;

function subscribeToFormReady(onReady: () => void) {
  const id = window.setTimeout(onReady, 0);
  return () => window.clearTimeout(id);
}

const formReadyClientSnapshot = () => true;
const formReadyServerSnapshot = () => false;

function useFormReady(initialFocusField?: KundaliField) {
  const ready = useSyncExternalStore(
    subscribeToFormReady,
    formReadyClientSnapshot,
    formReadyServerSnapshot
  );
  useInitialKundaliFieldFocus(initialFocusField, ready);
  return ready;
}

// Warm the result chunk the moment the seeker starts the form, so by the time
// they submit, the `dynamic(ssr:false)` chunk is already in the module cache and
// the result swaps in within a frame — no `Suspense` gap (CLS). `dynamic()` and
// this raw `import()` resolve the SAME webpack module, so one warms the other.
function usePreloadKundaliResultOnFormStart(formStarted: boolean) {
  useEffect(() => {
    if (formStarted) void import("@/components/KundaliResult");
  }, [formStarted]);
}

export type KundaliFormCardProps = {
  defaultLanguage?: KundaliLang;
  /**
   * An in-language regional edition (`/bn|gu|mr|or|ta/free-kundali/`). The
   * form and the computed chart render in this language; the kundali API,
   * analytics, chat and offers still receive `defaultLanguage`, so a regional
   * code never reaches an en/hi contract. Hides the en/hi toggle, which could
   * not switch back to this language.
   */
  displayLocale?: KundaliRegionalLocale;
  source?: string;
  submitLabel?: string;
  savedNote?: string;
  savedNoteHi?: string;
  phoneReason?: string;
  phoneReasonHi?: string;
  showDailyInsightCard?: boolean;
  containerClassName?: string;
  formClassName?: string;
  namePlaceholder?: string;
  placePlaceholder?: string;
  requireBirthTime?: boolean;
  reassuranceNote?: string;
  reassuranceNoteHi?: string;
  loadingLabel?: string;
  loadingLabelHi?: string;
  seoEntryOverride?: CapturedSEOEntry | null;
  progressiveCopy?: KundaliProgressiveCopy;
  initialFocusField?: KundaliField;
  /** Text typed into the server shell's field before this form mounted (C08). */
  initialFieldValue?: KundaliInitialFieldValue;
  showIntentBanner?: boolean;
  /** Hosts with a page-level language switch can hide this form-only duplicate. */
  showLanguageToggle?: boolean;
  /**
   * Optional override for what renders BELOW the inline kundali result on the
   * result-first layout. REPLACES the default saved-link + daily-panchang
   * adjuncts so a host surface can render its own intent-matched value-moment
   * block (e.g. the free career snapshot + the career_report depth gate on
   * /career-astrology). The inline result itself is unchanged — additive,
   * monetization stays on the value surface (rule 46), commits with the result
   * chunk so no card shifts in first (rule 58).
   */
  renderPostResult?: (result: KundaliResponse, language: KundaliLang) => ReactNode;
};

export default function KundaliFormCard({
  defaultLanguage = "en",
  displayLocale,
  source = "home",
  submitLabel,
  savedNote,
  savedNoteHi,
  phoneReason,
  phoneReasonHi,
  showDailyInsightCard = true,
  containerClassName = "mx-auto max-w-md px-4 sm:px-6 pb-16",
  formClassName,
  namePlaceholder,
  placePlaceholder,
  requireBirthTime = false,
  reassuranceNote,
  reassuranceNoteHi,
  loadingLabel,
  loadingLabelHi,
  seoEntryOverride,
  progressiveCopy,
  initialFocusField,
  initialFieldValue,
  showIntentBanner = true,
  showLanguageToggle = true,
  renderPostResult,
}: KundaliFormCardProps) {
  const router = useRouter();
  const [formId] = useState(createEventId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<KundaliFieldErrors>({});
  const [result, setResult] = useState<KundaliResponse | null>(null);
  const [language, setLanguage] = useState<KundaliLang>(defaultLanguage);
  const locale: KundaliLocale = displayLocale ?? language;
  const [formStarted, setFormStarted] = useState(false);
  const [formReady, loadingStep] = [useFormReady(initialFocusField), useLoadingStep(loading)];
  const showDailyInsight = useDeferredDailyInsight();
  const seoEntry = useResolvedSEOEntry(seoEntryOverride);
  const fields = useKundaliFieldState(
    setError,
    setFieldErrors,
    initialFieldValue,
    initialFocusField
  );
  const [retryState, setRetryState] = useState<KundaliRetryState>(null);
  const fieldFunnel = useFieldFunnel({
    source: seoEntry ? `seo:${seoEntry.config.intent}` : source,
    source_path: seoEntry?.sourcePath,
    language,
  });
  const markFormStarted = useMarkFormStarted({
    formId,
    setFormStarted,
    language,
    seoEntry,
    source,
  });
  const submitDeps = {
    formId,
    seoEntry,
    source,
    requireBirthTime,
    name: fields.name,
    phone: fields.phone,
    email: fields.email,
    birthDate: fields.birthDate,
    birthTime: fields.birthTime,
    birthPlace: fields.birthPlace,
    language,
    displayLocale,
    selectedPlace: fields.selectedPlace,
    rememberedPlace: fields.rememberedPlace,
    router,
    setLoading,
    setError,
    setFieldErrors,
    setResult,
    setSelectedPlace: fields.setSelectedPlace,
    setAmbiguousSuggestion: fields.setAmbiguousSuggestion,
    setRetryState,
  };
  const handleSubmit = makeHandleSubmit(submitDeps);
  const retryKundali = retryHandler(submitDeps, retryState);
  usePreloadKundaliResultOnFormStart(formStarted);
  const t = useKundaliTranslationBundle(locale, {
    submitLabel,
    savedNote,
    savedNoteHi,
    loadingLabel,
    loadingLabelHi,
    namePlaceholder,
    placePlaceholder,
    phoneReason,
    phoneReasonHi,
  });
  if (result)
    return renderKundaliResult(
      result,
      { language, locale },
      source,
      () => setResult(null),
      renderPostResult
    );

  return (
    <>
      <KundaliSpiritualReveal loading={loading} locale={locale} loadingStep={loadingStep} />
      <KundaliInputForm
        containerClassName={containerClassName}
        formClassName={formClassName}
        onSubmit={handleSubmit}
        seoEntry={formBannerSEOEntry(seoEntry, source)}
        language={language}
        locale={locale}
        setLanguage={setLanguage}
        formReady={formReady}
        {...fields}
        requireBirthTime={requireBirthTime}
        onClearPlace={fields.clearPlace}
        fieldFunnel={fieldFunnel}
        markFormStarted={markFormStarted}
        fieldErrors={fieldErrors}
        error={error}
        loading={loading}
        reassuranceNote={localizedOverride(language, reassuranceNote, reassuranceNoteHi)}
        deliveryField={null} // ADR-0026 second contact field removed 2026-06-23 (rule 39)
        submitDisabled={!formReady}
        retryCard={renderRetryCard(retryState, retryKundali, loading, t)}
        submitLabelOverride={submitLabel}
        t={t}
        progressiveCopy={progressiveCopy}
        source={seoEntry ? `seo:${seoEntry.config.intent}` : source}
        sourcePath={seoEntry?.sourcePath}
        initialFocusField={initialFocusField}
        showIntentBanner={showIntentBanner}
        showLanguageToggle={showLanguageToggle && !displayLocale}
      />
      <DailyInsightSlot show={showDailyInsightCard} ready={showDailyInsight} language={language} />
    </>
  );
}

function useResolvedSEOEntry(override: CapturedSEOEntry | null | undefined) {
  const captured = useCapturedSEOEntry(override);
  return override !== undefined ? override : captured;
}

function renderRetryCard(
  retryState: KundaliRetryState,
  retryKundali: (() => void) | null,
  loading: boolean,
  t: ReturnType<typeof getKundaliTranslations>
) {
  return (
    <RetryCardSlot retryState={retryState} retryKundali={retryKundali} loading={loading} t={t} />
  );
}

// Extracted so the main component stays under max-lines-per-function; renders the
// retry card only when a retryable failure is in flight.
function RetryCardSlot({
  retryState,
  retryKundali,
  loading,
  t,
}: {
  retryState: KundaliRetryState;
  retryKundali: (() => void) | null;
  loading: boolean;
  t: ReturnType<typeof getKundaliTranslations>;
}) {
  if (!retryState || !retryKundali) return null;
  return (
    <KundaliRetryCard
      title={t.retryTitle}
      body={t.retryBody}
      actionLabel={t.retryAction}
      onRetry={retryKundali}
      loading={loading}
    />
  );
}

function localizedOverride(language: KundaliLang, en?: string, hi?: string): string | undefined {
  return language === "hi" ? (hi ?? en) : en;
}

function useMarkFormStarted(args: {
  formId: string;
  onStarted?: (firstField: string) => void;
  setFormStarted: (value: boolean) => void;
  language: KundaliLang;
  seoEntry: CapturedSEOEntry | null;
  source: string;
}) {
  const { formId, setFormStarted, language, seoEntry, source, onStarted } = args;
  const startedRef = useRef(false);
  return useCallback(
    (firstField: string) => {
      if (startedRef.current) return;
      startedRef.current = true;
      setFormStarted(true);
      onStarted?.(firstField);
      trackKundaliFormStarted({
        form_id: formId,
        language,
        first_field: firstField,
        source: seoEntry ? `seo:${seoEntry.config.intent}` : source,
        source_path:
          seoEntry?.sourcePath ||
          (typeof window !== "undefined" ? window.location.pathname || undefined : undefined),
      });
    },
    [formId, setFormStarted, language, seoEntry, source, onStarted]
  );
}

function renderKundaliResult(
  result: KundaliResponse,
  { language, locale }: { language: KundaliLang; locale: KundaliLocale },
  source: string,
  onBack: () => void,
  renderPostResult?: (result: KundaliResponse, language: KundaliLang) => ReactNode
) {
  const layout = source === "ai_astrologer" ? "chat-first" : "result-first";
  const postResultAdjuncts =
    layout === "result-first"
      ? (renderPostResult?.(result, language) ??
        (result.id ? <KundaliPostResultAdjuncts result={result} language={language} /> : null))
      : null;
  const content = (
    <KundaliResultBoundary
      result={result}
      language={language}
      locale={locale}
      onBack={onBack}
      layout={layout}
      postResultAdjuncts={postResultAdjuncts}
    />
  );
  if (layout !== "chat-first") {
    return content;
  }
  return <div className="ai-chat-result-active">{content}</div>;
}

function KundaliPostResultAdjuncts({
  result,
  language,
}: {
  result: KundaliResponse;
  language: KundaliLang;
}) {
  // Result-first entries keep the inline result AND get its durable return
  // path: every generated kundali is persisted under result.id, and
  // /kundali/<id> re-renders the same KundaliResult tree (rule 46 — the
  // inline value moment stays put; the saved link is additive). Keep these
  // adjuncts inside KundaliResult so the dynamic result chunk and cards commit
  // together instead of the cards rendering first and shifting downward.
  return (
    <div style={{ minHeight: POST_RESULT_ADJUNCTS_MIN_HEIGHT }}>
      <KundaliSavedLink readingId={result.id} language={language} />
      <div style={{ minHeight: DAILY_PANCHANG_LINK_MIN_HEIGHT }}>
        <DailyPanchangLink
          readingId={result.id}
          language={language === "hi" ? "hi" : "en"}
          source="kundali_result"
        />
      </div>
    </div>
  );
}

function useLoadingStep(loading: boolean) {
  const [loadingStep, setLoadingStep] = useState(0);
  useEffect(() => {
    startTransition(() => setLoadingStep(0));
    if (!loading) return;
    const timers = loadingStatusTimers(setLoadingStep);
    return () => timers.forEach(clearTimeout);
  }, [loading]);
  return loadingStep;
}

function KundaliSpiritualReveal({
  loading,
  locale,
  loadingStep,
}: {
  loading: boolean;
  locale: KundaliLocale;
  loadingStep: number;
}) {
  if (!loading) return null;
  if (locale !== "en" && locale !== "hi") {
    // The shared reveal reads `steps[i][language]` for en|hi only, so a
    // regional edition hands it its own lines under the `en` slot.
    const steps = REGIONAL_KUNDALI_FORM_COPY[locale].loadingSteps.map((line) => ({
      en: line,
      hi: line,
    }));
    return <SpiritualReveal loading language="en" steps={steps} currentStep={loadingStep} />;
  }
  return (
    <SpiritualReveal
      loading
      language={locale}
      steps={LOADING_STATUS_MESSAGES}
      currentStep={loadingStep}
    />
  );
}

function retryHandler(deps: HandleSubmitDeps, retry: KundaliRetryState) {
  return retry ? makeRetryKundali(deps, retry.idempotencyKey) : null;
}
